import { useState } from "react";
import { UserPlus, ArrowLeft } from "lucide-react";
import { Button, FormField, Input, Label } from "@/design-system/components/ui";
import { PartyPicker, type SelectedParty } from "@/components/parties/PartyPicker";
import { partiesApi } from "@/api/parties";
import { useCreateParty } from "@/hooks/parties/useParties";
import { ApiError } from "@/api/client";
import { displayEmail } from "@/design-system/lib/format";
import { cn } from "@/design-system/lib/utils";
import type { CreatePartyRequest, PartyKind, PartyType } from "@/types/parties";

// New Deal wizard — Borrower/Seller party lookup + inline create-new.
//
// Borrower/Seller are Party-first (counterparty sunset). This composes the
// shared PartyPicker (search the Borrower/Seller registry) with an inline
// quick-create that collects EXACTLY the party's required fields — email +
// (person: first/last | business: legal name) — nothing optional. On the
// server's globally-unique-email conflict it offers the existing record
// instead of failing (parties have no DedupCheckModal; email IS the identity).

interface Props {
  type: PartyType;
  value: SelectedParty | null;
  onChange: (value: SelectedParty | null) => void;
  label: string;
}

export function DealPartyLookup({ type, value, onChange, label }: Props) {
  const [creating, setCreating] = useState(false);
  const noun = type.toLowerCase();

  if (creating) {
    return (
      <InlinePartyCreate
        type={type}
        label={label}
        onCreated={(party) => {
          onChange(party);
          setCreating(false);
        }}
        onCancel={() => setCreating(false)}
      />
    );
  }

  return (
    <div className="flex flex-col gap-space-1">
      <PartyPicker type={type} value={value} onChange={onChange} label={label} />
      {!value && (
        <button
          type="button"
          onClick={() => setCreating(true)}
          className="self-start inline-flex items-center gap-space-1 text-caption uppercase tracking-wide text-gold-shadow hover:underline"
          data-testid={`create-${noun}`}
        >
          <UserPlus size={12} aria-hidden /> Create new {noun}
        </button>
      )}
    </div>
  );
}

function InlinePartyCreate({
  type,
  label,
  onCreated,
  onCancel,
}: {
  type: PartyType;
  label: string;
  onCreated: (party: SelectedParty) => void;
  onCancel: () => void;
}) {
  const [partyKind, setPartyKind] = useState<PartyKind>("Person");
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [legalName, setLegalName] = useState("");
  const [email, setEmail] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [existing, setExisting] = useState<SelectedParty | null>(null);

  const noun = type.toLowerCase();
  const trimmedEmail = email.trim();

  const requiredComplete =
    !!trimmedEmail &&
    (partyKind === "Person"
      ? !!firstName.trim() && !!lastName.trim()
      : !!legalName.trim());

  // Reuse the canonical hook — it invalidates the parties + portal-absorption
  // caches on success so a freshly created borrower/seller shows everywhere.
  const create = useCreateParty(type);

  function submit() {
    setError(null);
    setExisting(null);
    const body: CreatePartyRequest =
      partyKind === "Person"
        ? {
            partyKind,
            email: trimmedEmail,
            firstName: firstName.trim(),
            lastName: lastName.trim(),
          }
        : { partyKind, email: trimmedEmail, legalName: legalName.trim() };
    create.mutate(body, {
      onSuccess: (p) =>
        onCreated({ id: p.id, name: p.displayName, detail: displayEmail(p.email) }),
      onError: async (err) => {
        // The party registry enforces a globally-unique email — on a conflict,
        // find the record that already owns it and offer it instead of erroring.
        const message =
          err instanceof ApiError
            ? [err.message, ...err.errors].join(" ")
            : err instanceof Error
              ? err.message
              : "";
        if (/email/i.test(message) && /(exist|already|use|taken)/i.test(message)) {
          try {
            const page = await partiesApi.list(type, { search: trimmedEmail, limit: 5 });
            const match = page.data.find(
              (p) => p.email.toLowerCase() === trimmedEmail.toLowerCase(),
            );
            if (match) {
              setExisting({
                id: match.id,
                name: match.displayName,
                detail: displayEmail(match.email),
              });
              return;
            }
          } catch {
            /* fall through to the raw message */
          }
        }
        setError(
          err instanceof ApiError && err.errors.length
            ? err.errors.join("; ")
            : err instanceof Error
              ? err.message
              : "Could not create.",
        );
      },
    });
  }

  return (
    <div className="flex flex-col gap-space-2 rounded-md border border-border-light bg-white-warm p-space-3">
      <div className="flex items-center justify-between">
        <Label>New {label.toLowerCase()}</Label>
        <button
          type="button"
          onClick={onCancel}
          className="inline-flex items-center gap-space-1 text-caption uppercase tracking-wide text-gray-mid hover:text-onyx"
        >
          <ArrowLeft size={12} aria-hidden /> Back to search
        </button>
      </div>

      <div className="flex gap-space-1" role="group" aria-label={`${noun} type`}>
        {(["Person", "Business"] as PartyKind[]).map((k) => (
          <button
            key={k}
            type="button"
            onClick={() => setPartyKind(k)}
            className={cn(
              "flex-1 rounded-md border px-space-2 py-space-1 text-small transition",
              partyKind === k
                ? "border-gold bg-gold/10 text-onyx font-semibold"
                : "border-border-light bg-white-pure text-gray-mid hover:border-gold",
            )}
            aria-pressed={partyKind === k}
          >
            {k === "Business" ? "Business / Entity" : "Person"}
          </button>
        ))}
      </div>

      {partyKind === "Person" ? (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-space-2">
          <FormField label="First name" required htmlFor={`${noun}-first`}>
            <Input
              id={`${noun}-first`}
              value={firstName}
              onChange={(e) => setFirstName(e.target.value)}
            />
          </FormField>
          <FormField label="Last name" required htmlFor={`${noun}-last`}>
            <Input
              id={`${noun}-last`}
              value={lastName}
              onChange={(e) => setLastName(e.target.value)}
            />
          </FormField>
        </div>
      ) : (
        <FormField label="Legal entity name" required htmlFor={`${noun}-legal`}>
          <Input
            id={`${noun}-legal`}
            value={legalName}
            onChange={(e) => setLegalName(e.target.value)}
          />
        </FormField>
      )}

      <FormField label="Email" required htmlFor={`${noun}-email`}>
        <Input
          id={`${noun}-email`}
          type="email"
          value={email}
          onChange={(e) => {
            setEmail(e.target.value);
            setExisting(null);
            setError(null);
          }}
          placeholder="name@example.com"
        />
      </FormField>

      {existing && (
        <div className="flex items-center justify-between gap-space-2 rounded-md border border-gold bg-gold/5 p-space-2">
          <span className="text-small text-onyx">
            A {noun} already exists with this email:{" "}
            <span className="font-semibold">{existing.name}</span>.
          </span>
          <Button size="sm" onClick={() => onCreated(existing)}>
            Use existing
          </Button>
        </div>
      )}

      {error && (
        <p className="text-small text-status-error m-0" role="alert">
          {error}
        </p>
      )}

      <div className="flex justify-end">
        <Button onClick={submit} disabled={!requiredComplete} loading={create.isPending}>
          Create {noun}
        </Button>
      </div>
    </div>
  );
}
